/**
 * Вкладка «Подбор» «Голосового стенда» (`?tab=select`): проба инструмента робота `find_products` — запрос
 * клиента своими словами → что понял фильтр «Резерва», чего не понял, сколько моделей в салоне города, из чего
 * выбирать, фраза робота и ссылка на подборку. Ниже — журнал всех запросов (вкладка, звонки) с оценкой 👍/👎.
 * Разбор — Haiku по API (`PROKAT_SELECT_LLM_API`); выключен — запрос ждёт модель в сессии («ждёт разбора»).
 * Запрос можно надиктовать: запись → общий голос кабинета (`/api/voice`) → расшифровка в поле → разбор.
 * Ниже — «Как ищут в Яндексе»: топ Wordstat по свадебным, вечерним, выпускным с итогом подбора каждой фразы
 * (`GET /api/voice-lab/select/keywords`, команда `select-wordstat`). `POST|GET /api/voice-lab/select`.
 * Спека `docs/specs/2026-10-27-product-select-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { VoiceControl, useVoice, voiceBusy } from "@/components/voice/VoiceInput";
import { loadVoices, uploadVoice } from "@/lib/voice";
import {
  type SelectKeyword,
  type SelectRow,
  labSelect,
  labSelectKeywordsQuery,
  labSelectQuery,
  labSelectVerdict,
} from "@/lib/voiceLab";

const СТАТУС: Record<string, string> = {
  pending: "ждёт разбора",
  parsed: "ищу",
  done: "готово",
  failed: "не вышло",
};
const ИСТОЧНИК: Record<string, string> = { lab: "вкладка", voice: "звонок", chat: "чаты", eval: "эталон" };
const ПРИМЕРЫ = [
  "у вас есть асимметричное платье 48-го размера?",
  "пышное свадебное с длинным рукавом",
  "нарядное платье на девочку 7 лет",
  "синий костюм жениху 50",
  "вечернее в пол, только не красное",
];

function Список({ имя, что, тон }: { имя: string; что?: string[] | undefined; тон?: string }) {
  if (!что?.length) return null;
  return (
    <div className={тон}>
      <span className="text-fg-muted">{имя}: </span>
      {что.join(" · ")}
    </div>
  );
}

function Ответ({ r }: { r: SelectRow }) {
  const о = r.result;
  if (r.status === "pending" || r.status === "parsed")
    return <div className="text-fg-muted">{СТАТУС[r.status]}…</div>;
  if (r.status === "failed") return <div className="text-err">{r.error || "не вышло"}</div>;
  if (!о) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[13.5px]">«{о.say}»</div>
      {!!о.filters?.length && (
        <div className="flex flex-wrap gap-1">
          {о.filters.map((ф) => (
            <span key={ф} className="rounded-full border border-border px-2 py-0.5 text-[11.5px]">
              {ф}
            </span>
          ))}
        </div>
      )}
      <Список имя="не применил" что={о.lost} тон="text-err" />
      <Список имя="не умею" что={о.unsupported} тон="text-err" />
      <Список имя="спросить" что={о.ask?.map((a) => ({ city: "город", category: "что ищет", size: "размер" })[a] ?? a)} />
      <Список имя="на выбор не влияет" что={о.unknown} />
      {о.total != null && (
        <div>
          <span className="text-fg-muted">моделей в городе: </span>
          <b>{о.total}</b>
          {о.price_from ? <span className="text-fg-muted"> · прокат от {о.price_from.toLocaleString("ru")} ₽</span> : null}
          {о.complete === false && <span className="text-err"> · число роботу не озвучено: понял не всё</span>}
        </div>
      )}
      {Object.entries(о.facets ?? {}).map(([г, з]) => (
        <div key={г} className="text-fg-muted">
          {г}: {з.map(([в, n]) => `${в} ${n}`).join(", ")}
        </div>
      ))}
      {о.alternatives?.map((а) => (
        <div key={а.without} className="text-fg-muted">
          без «{а.without}» — {а.total}
        </div>
      ))}
      {о.link && (
        <a href={о.link} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          подборка для клиента ↗
        </a>
      )}
    </div>
  );
}

function Оценка({ r }: { r: SelectRow }) {
  const qc = useQueryClient();
  const [замечание, setЗамечание] = useState(r.comment ?? "");
  const m = useMutation({
    mutationFn: (verdict: "ok" | "bad" | null) => labSelectVerdict(r.id, { verdict, comment: замечание || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voice-lab-select"] }),
  });
  const кнопка = (v: "ok" | "bad", знак: string) => (
    <button
      type="button"
      onClick={() => m.mutate(r.verdict === v ? null : v)}
      className={`h-7 w-8 rounded-[var(--radius-ui)] border text-[13px] ${r.verdict === v ? "border-accent bg-accent/10" : "border-border opacity-60 hover:opacity-100"}`}
    >
      {знак}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5">
      {кнопка("ok", "👍")}
      {кнопка("bad", "👎")}
      <input
        value={замечание}
        onChange={(e) => setЗамечание(e.target.value)}
        onBlur={() => замечание !== (r.comment ?? "") && m.mutate(r.verdict)}
        placeholder="замечание"
        className="h-7 min-w-0 flex-1 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[12px]"
      />
    </div>
  );
}

export function VoiceLabSelect({ cities }: { cities: { short: string; name: string }[] }) {
  const qc = useQueryClient();
  const [текст, setТекст] = useState("");
  const [город, setГород] = useState("МСК");
  const q = useQuery({
    ...labSelectQuery(),
    refetchInterval: (query) =>
      query.state.data?.rows.some((r) => r.status === "pending" || r.status === "parsed") ? 3000 : false,
  });
  const m = useMutation({
    mutationFn: (t: string) => labSelect({ text: t.trim(), city: город || null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voice-lab-select"] }),
  });
  // Голос: запись уходит сразу после «Стоп», расшифровку пишет фон — ждём её, кладём в поле и разбираем.
  const [голос, setГолос] = useState<number | null>(null);
  const [ошибкаГолоса, setОшибкаГолоса] = useState("");
  const voice = useVoice({ onDone: async (b, sec) => setГолос((await uploadVoice(b, sec)).id) });
  const расшифровка = useQuery({
    queryKey: ["voice", голос],
    queryFn: () => loadVoices([голос as number]),
    enabled: голос != null,
    refetchInterval: 1000,
  });
  const запись = расшифровка.data?.[0];
  const разобрать = useRef(m.mutate);
  разобрать.current = m.mutate;
  useEffect(() => {
    if (!запись || (запись.status !== "done" && запись.status !== "failed")) return;
    setГолос(null);
    setОшибкаГолоса(запись.status === "failed" ? запись.error || "попробуйте ещё раз" : "");
    if (запись.status === "done" && запись.text.trim().length >= 2) {
      setТекст(запись.text.trim());
      разобрать.current(запись.text);
    }
  }, [запись]);
  const строки = q.data?.rows ?? [];
  return (
    <section className="flex max-w-4xl flex-col gap-4 text-[12.5px]" data-annot="подбор товара">
      <div className="text-fg-muted">
        Запрос клиента своими словами или голосом → фильтр каталога «Резерва» → что робот скажет в звонке. Разбор — Haiku по API; если
        его выключить, запрос ждёт модель в сессии («разбери подбор» в чате).
      </div>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (текст.trim().length >= 2) m.mutate(текст);
        }}
      >
        <div className="flex flex-wrap gap-2">
          <input
            value={текст}
            onChange={(e) => setТекст(e.target.value)}
            placeholder="у вас есть асимметричное платье 48-го?"
            maxLength={300}
            className="h-9 min-w-0 flex-1 rounded-[var(--radius-ui)] border border-border bg-transparent px-3 text-[13px]"
          />
          <select
            value={город}
            onChange={(e) => setГород(e.target.value)}
            className="h-9 rounded-[var(--radius-ui)] border border-border bg-transparent px-2"
          >
            <option value="">без города</option>
            {cities.map((c) => (
              <option key={c.short} value={c.short}>
                {c.name}
              </option>
            ))}
          </select>
          <VoiceControl voice={voice} compact disabled={голос != null} sentText="" />
          <button
            type="submit"
            disabled={m.isPending || voiceBusy(voice.v) || текст.trim().length < 2}
            className="h-9 rounded-[var(--radius-ui)] border border-accent px-4 text-accent disabled:opacity-50"
          >
            Разобрать
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ПРИМЕРЫ.map((п) => (
            <button
              key={п}
              type="button"
              onClick={() => setТекст(п)}
              className="rounded-full border border-border px-2.5 py-0.5 text-[12px] text-fg-muted hover:text-fg"
            >
              {п}
            </button>
          ))}
        </div>
        {голос != null && <div className="text-fg-muted">Расшифровываю голос…</div>}
        {ошибкаГолоса && <div className="text-err">Голос не расшифровался: {ошибкаГолоса}</div>}
        {m.error && <div className="text-err">{String(m.error)}</div>}
      </form>
      <Wordstat
        onPick={(t) => {
          setТекст(t);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      {q.isError && <div className="text-err">Журнал не загрузился</div>}
      <div className="flex flex-col">
        {строки.map((r) => (
          <div key={r.id} className="grid gap-x-4 gap-y-1.5 border-b border-border py-2.5 md:grid-cols-[1fr_1.4fr]">
            <div className="flex flex-col gap-1">
              <div className="text-[13px]">{r.text}</div>
              <div className="text-[11.5px] text-fg-muted">
                {r.date.slice(5, 16)} · {r.city ?? "без города"} · {ИСТОЧНИК[r.source] ?? r.source}
                {r.mode ? ` · разбор: ${r.mode}` : ""}
                {r.parse_ms != null ? ` · ${(r.parse_ms / 1000).toFixed(1)} с` : ""}
                {r.search_ms != null ? ` + поиск ${(r.search_ms / 1000).toFixed(1)} с` : ""}
              </div>
              {r.status === "done" && <Оценка r={r} />}
            </div>
            <Ответ r={r} />
          </div>
        ))}
        {!q.isLoading && !строки.length && <div className="text-fg-muted">Запросов ещё не было</div>}
      </div>
    </section>
  );
}

const тыс = (n: number) => n.toLocaleString("ru");

function ИтогФразы({ k }: { k: SelectKeyword }) {
  if (k.noise) return <span className="text-fg-muted">не про товар: {k.noise}</span>;
  if (!k.status) return <span className="text-fg-muted">не прогоняли</span>;
  if (k.status !== "done") return <span className="text-fg-muted">{СТАТУС[k.status] ?? k.status}</span>;
  const не = [...(k.lost ?? []), ...(k.unsupported ?? [])];
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className={k.complete ? "font-medium" : "text-err"} title={k.say ?? ""}>
        {k.complete ? `✓ ${k.total ?? 0}` : "уточнит"}
      </span>
      {(k.filters ?? []).map((ф) => (
        <span key={ф} className="rounded-full border border-border px-1.5 text-[11px]">
          {ф}
        </span>
      ))}
      {!!не.length && <span className="text-[11.5px] text-err">не применил: {не.join(", ")}</span>}
      {!!k.ask?.length && <span className="text-[11.5px] text-err">спросит: {k.ask.join(", ")}</span>}
      {!!k.unknown?.length && <span className="text-[11.5px] text-fg-muted">не влияет: {k.unknown.join(", ")}</span>}
      {k.link && (
        <a href={k.link} target="_blank" rel="noreferrer" className="text-[11.5px] text-accent hover:underline">
          подборка ↗
        </a>
      )}
    </div>
  );
}

/** «Как ищут в Яндексе»: топ Wordstat затравки и как каждая фраза легла на фильтр «Резерва». */
function Wordstat({ onPick }: { onPick: (t: string) => void }) {
  const q = useQuery({ ...labSelectKeywordsQuery(), refetchInterval: 15000 });
  const [группа, setГруппа] = useState("wedding");
  const [всё, setВсё] = useState(false);
  const группы = q.data?.groups ?? [];
  const г = группы.find((x) => x.seed === группа) ?? группы[0];
  if (!г?.rows.length) return null;
  const товарные = г.rows.filter((r) => !r.noise);
  const понятые = товарные.filter((r) => r.status === "done" && r.complete);
  const сумма = (rr: SelectKeyword[]) => rr.reduce((a, r) => a + r.count, 0);
  const видно = всё ? г.rows : товарные;
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3" data-annot="популярные запросы Wordstat">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <b className="text-[13px]">Как ищут в Яндексе</b>
        <span className="text-fg-muted">
          Wordstat, вся Россия, {г.period?.[0].slice(5).split("-").reverse().join(".")}–
          {г.period?.[1].slice(5).split("-").reverse().join(".")}; клик по фразе — в поле запроса
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {группы.map((x) => (
          <button
            key={x.seed}
            type="button"
            onClick={() => setГруппа(x.seed)}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] ${x.seed === г.seed ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
            title={x.seeds.join(" + ")}
          >
            {x.name} · {x.rows.length}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1 text-[12px] text-fg-muted">
          <input type="checkbox" checked={всё} onChange={(e) => setВсё(e.target.checked)} /> показать не про товар
        </label>
      </div>
      <div className="text-fg-muted">
        Товарных фраз {товарные.length} из {г.rows.length} ({тыс(сумма(товарные))} запросов); понято целиком {понятые.length} —{" "}
        {сумма(товарные) ? Math.round((сумма(понятые) / сумма(товарные)) * 100) : 0}% товарного спроса
      </div>
      <div className="flex flex-col">
        {видно.map((k) => (
          <div
            key={k.id}
            className={`grid grid-cols-[2rem_4.5rem_1fr] gap-x-2 border-b border-border py-1.5 md:grid-cols-[2rem_4.5rem_minmax(0,0.9fr)_minmax(0,1.4fr)] ${k.noise ? "opacity-50" : ""}`}
          >
            <span className="text-fg-muted tabular-nums">{k.rank}</span>
            <span className="text-right tabular-nums">{тыс(k.count)}</span>
            <button type="button" onClick={() => onPick(k.text)} className="text-left hover:text-accent">
              {k.text}
            </button>
            <div className="col-span-3 md:col-span-1">
              <ИтогФразы k={k} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
